/**
 * Cookie consent state — persisted in localStorage.
 *
 * Categories:
 *   - essential: always true (functional cookies, cart, auth)
 *   - analytics: enables Google Analytics 4
 *   - marketing: enables Meta Pixel + TikTok Pixel (and their CAPI mirrors)
 *
 * Until the user makes a decision, `hasDecided` is false and the banner shows.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "opik_cookie_consent_v1";

const ConsentContext = createContext({
  consent: null,
  hasDecided: false,
  acceptAll: () => {},
  rejectAll: () => {},
  update: () => {},
  manageOpen: false,
  openManage: () => {},
  closeManage: () => {},
});

const readStored = () => {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch (e) { /* ignore */ }
  return null;
};

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(readStored);
  const [manageOpen, setManageOpen] = useState(false);

  // Notify listeners (e.g. App-level pixel init) whenever consent changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("opik:consent-change", { detail: consent }));
  }, [consent]);

  const persist = useCallback((next) => {
    const payload = {
      essential: true,
      analytics: !!next.analytics,
      marketing: !!next.marketing,
      timestamp: new Date().toISOString(),
      version: 1,
    };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) { /* ignore */ }
    setConsent(payload);
  }, []);

  const acceptAll = useCallback(() => persist({ analytics: true, marketing: true }), [persist]);
  const rejectAll = useCallback(() => persist({ analytics: false, marketing: false }), [persist]);
  const update = useCallback((partial) => persist({
    analytics: partial.analytics ?? consent?.analytics ?? false,
    marketing: partial.marketing ?? consent?.marketing ?? false,
  }), [persist, consent]);

  const openManage = useCallback(() => setManageOpen(true), []);
  const closeManage = useCallback(() => setManageOpen(false), []);

  const value = useMemo(() => ({
    consent,
    hasDecided: !!consent,
    acceptAll,
    rejectAll,
    update,
    manageOpen,
    openManage,
    closeManage,
  }), [consent, acceptAll, rejectAll, update, manageOpen, openManage, closeManage]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);

export const readConsent = readStored;
